import { browserHistory } from 'react-router'
import { storage } from '../utils'
import * as api from '../constants/APIEndpoints'

const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500

/**
 * Given a prototype URL (i.e. one with with possible replacements), splice any the replacements in
 * from urlData and also append any queryString parts.
 *
 * Note that to get a querystring, you must use the special urlData key '?', and then its value
 * should be a hash:
 *
 *     url = generateURL('http://api.example.com/user/:id/posts', {
 *         id: 7,
 *         '?': {
 *             page: 2,
 *             per_page: 10,
 *         }
 *     }) // => http://api.example.com/user/7/posts?page=2&per_page=10
 *
 * If the URL already contains a querystring, the `?` values from `urlData` will simply be apended:
 *
 *     url = generateURL('http://api.example.com/user/:id/posts?per_page=10', {
 *         id: 7,
 *         '?': {
 *             page: 2,
 *         }
 *     }) // => http://api.example.com/user/7/posts?per_page=10&page=2
 *
 * @param {string} url - The prototype URL.
 * @param {object} urlData - The data to splice into the prototype URL.
 * @return {string} The new, usable URL
 */
function generateURL(url, urlData) {
    if (urlData) {
        const queryString = []

        for (const key in urlData) {
            if (key === '?') {
                for (const queryKey in urlData[key]) {
                    queryString.push(
                        `${ encodeURIComponent(queryKey) }=${ encodeURIComponent(urlData[key][queryKey]) }`
                    )
                }
            } else {
                url = url.replace(new RegExp(`:${ key }`, 'g'), urlData[key])
            }
        }

        // if we have some query string parts to add, add them
        if (queryString.length) {
            // first see if the URL already includes a query string
            if (url.includes('?')) {
                url += '&'
            } else {
                url += '?'
            }

            // now add the parts to the end
            url += queryString.join('&')
        }
    }

    return url
}

/**
 * Get an action creator thunk to perform a generic fetch.
 *
 * There are many options available here, and as many are optional, they should be passed in a parameters object rather
 * than as individual parameters.
 *
 * @param {string} type
 *     The type of request being made (within the convention for constants). This will be used to generate the URL and
 *     success/error action types.
 * @param {Object} urlData
 *     Any data that needs to be spliced into the URL by use of colon-prefixed placeholders (e.g. `:this`). This
 *     object's keys are the placeholders (without the colon) and values are what the placeholders should be replaced
 *     with.
 * @param {string} method
 *     The HTTP request method for the fetch request. Defaults to GET unless data is specified, in which case it'll use
 *     POST. To use any other method (e.g. PATCH or DELETE), it must be specified here.
 * @param {Object} data
 *     Any data to be send with the request. This object with be JSON stringified and sent as the request body.
 * @param {string} url
 *     The URL to use. Use this if you need to override the automatic URL detection based on the `type` parameter. This
 *     URL can contain the afroementioned placeholders, as the replacement is done after retrieving the URL from the API
 *     constants.
 * @param {Function} onSuccess
 *     A function to be executed when recieving a 'successful' (a.k.a 2xx) response from the API. This is primarily a
 *     way of being able to do pre-success-dispatch work and also to modify the success dispatch action. The function
 *     should return an object representing the action that should be dispatched as a result of the fetch call.
 * @param {Function} onError
 *     A function to be executed when recieving an 'unsuccessful' (a.k.a not 2xx) response from the API. Like
 *     `onSuccess` above, This is primarily a way of being able to do pre-error-dispatch work and also to modify the
 *     success dispatch action. The function should return an object representing the action that should be dispatched
 *     as a result of the fetch call.
 * @param {Array} onSuccessActions
 *     Any actions to be dispatched after a successful fetch. (These actions are dispatched after the normal success
 *     action dispatch that results from the fetch.)
 * @param {Array} onErrorActions
 *     Any actions to be dispatched after fetch that results in an error. (These actions are dispatched after the normal
 *     success action dispatch that results from the fetch.)
 * @param {Array} anyActions
 *     Any actions to be dispatched after any fetch. (These actions are dispatched after the nomal success action
 *     dispatch that results from the fetch.)
 * @param {boolean} shortcutPatch Whether to shortcut the patch and optimistically update.
 *
 * @returns {Function} An action creator thunk.
 */
export function performFetch({
    type,
    urlData = {},
    method = 'GET',
    data,
    url,
    onSuccess,
    onError,
    onSuccessActions = [],
    onErrorActions = [],
    anyActions = [],
    shortcutPatch = true,
}) {
    if (! type) {
        throw new Error('You must provide a type to performFetch.')
    }

    if (! url) {
        url = api[`${ type }_URL`]
    }
    if (! onSuccess) {
        onSuccess = `${ type }_SUCCESS`
    }
    if (! onError) {
        onError = `${ type }_ERROR`
    }

    // replace any placeholders in the URL
    url = generateURL(url, urlData)

    if ((method === 'GET') && data) {
        method = 'POST'
    }

    const request = {
        method,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    }

    if (storage.getItem('authToken')) {
        request.headers.Authorization = `Token ${ storage.getItem('authToken') }`
    }

    if (data) {
        request.body = JSON.stringify(data)
    }

    return (dispatch) => {
        let successAction = (data) => {
            // if we have any post-callback actions, execute them now
            if (onSuccessActions) {
                onSuccessActions.forEach((item) => dispatch(item))
            }
            if (anyActions) {
                anyActions.forEach((item) => dispatch(item))
            }

            if (onSuccess) {
                // if onSuccess is a function, call it and store whatever it returns
                // if it returns anything, then dispatch that (assuming it's in the correct format for dispatching),
                // if it returns nothing, dispatch a boilerplate action and pass in the data returned from the API

                if (typeof onSuccess === 'function') {
                    const dispatchData = onSuccess(data)

                    if (dispatchData) {
                        dispatch(dispatchData)
                    }
                } else {
                    dispatch({ type: onSuccess, data })
                }
            }
        }

        const errorAction = (errors) => {
            // if we have any post-callback actions, execute them now
            if (onErrorActions) {
                onErrorActions.forEach((item) => dispatch(item))
            }
            if (anyActions) {
                anyActions.forEach((item) => dispatch(item))
            }

            if (onError) {
                // see onSuccess above
                if (typeof onError === 'function') {
                    const dispatchData = onError(errors)

                    dispatch(dispatchData)
                } else {
                    dispatch({ type: onError, errors })
                }
            }
        }

        // Special case for PATCH requests - assume that the request will work fine and dispatch the
        // success action immediately and pass in the original data. Then set the success action to
        // an empty function so that the original action isn't dispatched when the response comes
        // back (as we already assumed it'd work). If the response is an error, the error action
        // will still fire, and technically the app will be in an inconsistent state, but that's not
        // a huge deal.
        if (shortcutPatch && (method === 'PATCH')) {
            successAction(data)
            successAction = () => {}
        }

        // tell the app that the current state is loading
        dispatch({ type: 'LOADING' })

        // perform fetch
        return fetch(
            url,
            request
        ).then((response) => {
            // tell the app that the current state is loading complete
            dispatch({ type: 'LOADING_COMPLETE' })

            // if the content type is JSON or and the original request was not a DELETE one (no data
            // comes back with DELETE) then we can say that the response is JSON
            const hasJSON = (response.headers.get('Content-Type') === 'application/json' && method !== 'DELETE')

            // see what the API said about what we sent it
            if (response.ok) {
                if (hasJSON) {
                    response.json().then((data) => {
                        successAction(data)
                    })
                } else {
                    successAction()
                }
            } else {
                // If status is unauthorised then we have to assume the user is 'logged out'
                if (response.status === HTTP_STATUS_UNAUTHORIZED) {
                    storage.removeItem('authToken')
                    storage.removeItem('impersonationToken')

                    browserHistory.push('/sign-in')

                    Raven && Raven.setUserContext()

                    dispatch({ type: 'AUTHENTICATE_ERROR', data })
                } else if (response.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
                    errorAction({
                        errors: {
                            __generic__: [ (
                                'There was an issue performing the request, please refresh the ' +
                                'browser and try again.'
                            ) ],
                        },
                    })
                } else {
                    if (hasJSON) {
                        response.json().then((data) => {
                            errorAction(data)
                        })
                    } else {
                        errorAction({
                            errors: {
                                __generic__: [ (
                                    'There was an issue performing the request, please refresh ' +
                                    'the browser and try again.'
                                ) ],
                            },
                        })
                    }
                }
            }
        }).catch((err) => {
            // any error actually performing the fetch itself...

            // tell the app that the current state is loading complete
            dispatch({ type: 'LOADING_COMPLETE' })

            if (err instanceof TypeError) {
                dispatch({ type: 'NETWORK_ERROR' })
            } else {
                console.error('Error loading data', err)
            }
        })
    }
}

export function performFileUpload({
    type,
    field,
    file,
    urlData = {},
    method = 'POST',
    data,
    url,
    onSuccess,
    onError,
    onProgress,
    onSuccessActions = [],
    onErrorActions = [],
    anyActions = [],
}) {
    if (! type) {
        throw new Error('You must provide a type to performFileUpload.')
    }
    if (! field) {
        throw new Error('You must provide a field to performFileUpload.')
    }
    if (! file) {
        throw new Error('You must provide a file to performFileUpload.')
    }

    if (! url) {
        url = api[`${ type }_URL`]
    }
    if (! onSuccess) {
        onSuccess = `${ type }_SUCCESS`
    }
    if (! onError) {
        onError = `${ type }_ERROR`
    }
    if (! onProgress) {
        onProgress = `${ type }_PROGRESS`
    }

    // replace any placeholders in the URL
    url = generateURL(url, urlData)

    // create a formdata object to handle the file
    const formData = new FormData()

    // add pased data into formData
    for (const key in data) {
        formData.append(key, data[key])
    }

    // now add the uploaded file to formData
    formData.append(field, file)

    return (dispatch) => {
        // tell the app that the current state is loading
        dispatch({ type: 'LOADING' })

        const xhr = new XMLHttpRequest()

        xhr.addEventListener('load', () => {
            dispatch({ type: 'LOADING_COMPLETE' })

            const successAction = (data) => {
                if (onSuccess) {
                    // if onSuccess is a function, call it and store whatever it returns
                    // if it returns anything, then dispatch that (assuming it's in the correct format for dispatching),
                    // if it returns nothing, dispatch a boilerplate action and pass in the data returned from the API

                    if (typeof onSuccess === 'function') {
                        const dispatchData = onSuccess(data)

                        dispatch(dispatchData)
                    } else {
                        dispatch({ type: onSuccess, data })
                    }
                }

                // if we have any post-callback actions, execute them now
                if (onSuccessActions) {
                    onSuccessActions.forEach((item) => dispatch(item))
                }
                if (anyActions) {
                    anyActions.forEach((item) => dispatch(item))
                }
            }

            const errorAction = (errors) => {
                if (onError) {
                    // see onSuccess above

                    if (typeof onError === 'function') {
                        const dispatchData = onError(errors)

                        dispatch(dispatchData)
                    } else {
                        dispatch({ type: onError, errors })
                    }
                }

                // if we have any post-callback actions, execute them now
                if (onErrorActions) {
                    onErrorActions.forEach((item) => dispatch(item))
                }
                if (anyActions) {
                    anyActions.forEach((item) => dispatch(item))
                }
            }

            // if the content type is JSON then we can say that the response is JSON
            const hasJSON = (xhr.getResponseHeader('Content-Type') === 'application/json')

            // see what the API said about what we sent it

            // eslint-disable-next-line no-magic-numbers
            if (xhr.status >= 200 && xhr.status < 300) {
                if (hasJSON) {
                    successAction(JSON.parse(xhr.responseText))
                } else {
                    successAction()
                }
            } else {
                if (hasJSON) {
                    errorAction(JSON.parse(xhr.responseText))
                } else {
                    errorAction()
                }
            }
        })

        xhr.addEventListener('error', () => {
            // any error actually performing the fetch itself...

            // tell the app that the current state is loading complete
            dispatch({ type: 'LOADING_COMPLETE' })

            console.error('File upload error.')
        })

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                // eslint-disable-next-line no-magic-numbers
                const percent = Math.round((event.loaded / event.total) * 100)

                if (typeof onProgress === 'function') {
                    onProgress(percent)
                } else {
                    dispatch({ type: onProgress, percent })
                }
            }
        })

        xhr.open(method, url)
        xhr.setRequestHeader('Accept', 'application/json')
        if (storage.getItem('authToken')) {
            xhr.setRequestHeader('Authorization', `Token ${ storage.getItem('authToken') }`)
        }
        xhr.send(formData)
    }
}

/**
 * Get an action creator thunk to submit an add/edit item form.
 *
 * @param {string} type The type of item being added/edited (within the convention for constants) with no "SUBMIT_"
 *     prefix.
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} item_uuid The UUID of the item to be edited (UUID of the specific line in the crew_whatever table,
 *     that is, not the UUID of the item itself), if editing.
 * @returns {Function} An action creator thunk.
 */
export function submitItem(type, data, crew_uuid, item_uuid) {
    if (! type) {
        throw new Error('You must provide a type to submitItem.')
    }

    let url = api[`ADD_${ type }_URL`]

    if (item_uuid) {
        url = api[`UPDATE_${ type }_URL`]
    }

    return performFetch({
        type: `SUBMIT_${ type }`,
        data,
        url,
        urlData: { crew_uuid, item_uuid },
        onSuccessActions: [
            { type: 'HIDE_MODAL' },
        ],
    })
}

/**
 * Get an action creator thunk to delete an item. Will call a DELETE request on the API url and on success/error, will
 * dispatch the appropriate DELETE_WHATEVER_SUCCESS action with the item UUID attached to any data that comes back with
 * the success/error response (as a DELETE REST call usually returns no data).
 *
 * @param {string} type - The type of item being deleted (within the convention for constants) with no "DELETE_" prefix.
 * @param {string} crew_uuid - The UUID of the crew member to delete the item from.
 * @param {string} item_uuid - The UUID of the item to be deleted (UUID of the specific line in the crew_whatever table,
 *     that is, not the UUID of the item itself).
 * @returns {Function} An action creator thunk.
 */
export function deleteItem(type, crew_uuid, item_uuid) {
    if (! type) {
        throw new Error('You must provide a type to deleteItem.')
    }

    return performFetch({
        type: `DELETE_${ type }`,
        method: 'DELETE',
        urlData: { crew_uuid, item_uuid },
        onSuccess: (data) => {
            return { type: `DELETE_${ type }_SUCCESS`, data: { ...data, uuid: item_uuid } }
        },
        onError: (data) => {
            return { type: `DELETE_${ type }_ERROR`, data: { ...data, uuid: item_uuid } }
        },
    })
}



// WEBPACK FOOTER //
// ./app/actions/utils.js